// src/components/charts/LineChart.js
import React, { useMemo, useRef, useState, useId } from 'react';
import './LineChart.css';

export default function LineChart({
  data = [],
  width = 360,
  height = 200,

  // Title row
  title,
  subtitle,          // string or React node
  titleRight,

  yMax,
  showPoints = true,
  formatX = (x) => (typeof x === 'string' ? x.slice(5) : x),
  formatY = (y) => y,
  tooltipFormat,
}) {

  const padding = { top: 24, right: 12, bottom: 20, left: 36 };

  const svgRef = useRef(null);
  const gradId = useId(); // ✅ unique gradient per chart instance

  const [hover, setHover] = useState(null); // { i }

  const {
    xs,
    ys,
    xScale,
    yScale,
    pathD,
    areaD,           // ✅ NEW
    ticksY,
    ticksX,
    innerW,
  } = useMemo(() => {
    const innerW = Math.max(1, width - padding.left - padding.right);
    const innerH = Math.max(1, height - padding.top - padding.bottom);

    const toNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

    const xs = data.map((d) => d?.x ?? '');
    const ys = data.map((d) => toNum(d?.y));

    const minY = 0;
    const maxYData = ys.length ? Math.max(...ys) : 0;
    const computedMax = yMax ?? Math.max(5, maxYData);
    const rangeY = Math.max(1, computedMax - minY);
    const stepY = Math.max(1, Math.ceil(computedMax / 4));

    const xScale = (i) =>
      padding.left + (i * innerW) / Math.max(1, xs.length - 1);

    const yScale = (vRaw) =>
      padding.top + innerH - ((toNum(vRaw) - minY) * innerH) / rangeY;

    const pathD =
      xs.length > 0
        ? xs
            .map((_, i) => `${i === 0 ? 'M' : 'L'} ${xScale(i)} ${yScale(ys[i])}`)
            .join(' ')
        : '';

    // ✅ Build the closed "area under the line" path
    const bottomY = padding.top + innerH;
    const areaD =
      xs.length > 0
        ? [
            `M ${xScale(0)} ${bottomY}`,
            `L ${xScale(0)} ${yScale(ys[0])}`,
            ...xs.slice(1).map((_, i) => `L ${xScale(i + 1)} ${yScale(ys[i + 1])}`),
            `L ${xScale(xs.length - 1)} ${bottomY}`,
            'Z',
          ].join(' ')
        : '';

    const ticksY = [];
    for (let v = 0; v <= computedMax; v += stepY) {
      ticksY.push({ v, y: yScale(v) });
    }

    const ticksX = [];
    const stride = xs.length > 0 ? Math.ceil(xs.length / 7) : 1;
    xs.forEach((label, i) => {
      if (i % stride === 0) ticksX.push({ i, x: xScale(i), label });
    });

    return { xs, ys, xScale, yScale, pathD, areaD, ticksY, ticksX, innerW };
  }, [data, width, height, yMax]);

  const nearestIndexFromClientX = (clientX) => {
    if (!svgRef.current) return null;
    const rect = svgRef.current.getBoundingClientRect();
    const localX = clientX - rect.left;
    if (xs.length <= 1) return 0;
    const t = (localX - padding.left) / Math.max(1, innerW);
    return Math.min(xs.length - 1, Math.max(0, Math.round(t * (xs.length - 1))));
  };

  const handleMove = (clientX) => {
    const i = nearestIndexFromClientX(clientX);
    setHover({ i });
  };

  const onMouseMove = (e) => handleMove(e.clientX);
  const onTouchMove = (e) => {
    if (e.touches?.length) handleMove(e.touches[0].clientX);
  };
  const onLeave = () => setHover(null);

  // Tooltip content (fixed position)
  let tipContent = null;
  if (hover) {
    const i = hover.i;

    const datum = { x: xs[i], y: ys[i], meta: data?.[i]?.meta };

    const defaultTip = { title: String(xs[i]), lines: [`Value: ${formatY(ys[i])}`] };
    const formatted = tooltipFormat ? tooltipFormat(datum) : defaultTip;

    tipContent = (
      <div className="lc-tip-box">
        <div className="lc-tip-title">{formatted.title}</div>
        {formatted.lines?.map((ln, idx) => (
          <div key={idx} className="lc-tip-line">
            {ln}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="linechart-wrap" style={{ width }}>
      {(title || titleRight || subtitle) && (
        <div className="linechart-titleBlock">
          <div className="linechart-titleRow">
            <div className="linechart-title">{title}</div>
            {titleRight ? <div className="linechart-titleRight">{titleRight}</div> : null}
          </div>
          {subtitle ? <div className="linechart-subtitle">{subtitle}</div> : null}
        </div>
      )}

      {tipContent}

      <div className="linechart-inner">
        <svg
          ref={svgRef}
          width={width}
          height={height}
          className="linechart-svg"
          onMouseMove={onMouseMove}
          onMouseLeave={onLeave}
          onTouchStart={onTouchMove}
          onTouchMove={onTouchMove}
          onTouchEnd={onLeave}
        >
          {/* ✅ Gradient definition */}
          <defs>
            <linearGradient id={`lcFill-${gradId}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#4a8df8" stopOpacity="0.22" />
              <stop offset="70%" stopColor="#4a8df8" stopOpacity="0.08" />
              <stop offset="100%" stopColor="#4a8df8" stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* Axes */}
          <line
            x1={padding.left}
            y1={height - padding.bottom}
            x2={width - padding.right}
            y2={height - padding.bottom}
            className="axis"
          />
          <line
            x1={padding.left}
            y1={padding.top}
            x2={padding.left}
            y2={height - padding.bottom}
            className="axis"
          />

          {/* Y grid + labels */}
          {ticksY.map((t, i) => (
            <g key={i}>
              <line
                x1={padding.left}
                y1={t.y}
                x2={width - padding.right}
                y2={t.y}
                className="grid"
              />
              <text
                x={padding.left - 8}
                y={t.y + 4}
                className="tick"
                textAnchor="end"
              >
                {formatY(t.v)}
              </text>
            </g>
          ))}


{/* X ticks (no labels) */}
{ticksX.map((t, i) => (
  <line
    key={i}
    x1={t.x}
    y1={height - padding.bottom}
    x2={t.x}
    y2={height - padding.bottom + 4}
    className="tick-line"
  />
))}



          {/* ✅ Area fill FIRST (so line stays on top) */}
          {areaD ? (
            <path
              d={areaD}
              className="area"
              fill={`url(#lcFill-${gradId})`}
              stroke="none"
            />
          ) : null}

          {/* Line */}
          <path d={pathD} className="line" fill="none" />

          {/* Points */}
          {showPoints &&
            xs.map((_, i) => (
              <circle
                key={i}
                cx={xScale(i)}
                cy={yScale(ys[i])}
                r="2.5"
                className="point"
              />
            ))}

          {/* Hover guideline + point */}
          {hover && (
            <>
              <line
                x1={xScale(hover.i)}
                y1={padding.top}
                x2={xScale(hover.i)}
                y2={height - padding.bottom}
                className="hover-line"
              />
              <circle
                cx={xScale(hover.i)}
                cy={yScale(ys[hover.i])}
                r="4.5"
                className="hover-point"
              />
            </>
          )}
        </svg>
      </div>
    </div>
  );
}
